import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Lightbulb } from 'lucide-react';
import { useDarkMode } from '../DarkModeContext';
import { useHelp } from './TourContext';

const HoverTooltip = ({ target, title, content, label, labelText, tip, additionalInfo, isActive }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const { isDarkMode } = useDarkMode();
  const { activeTooltipId, setActiveTooltipId, animateRobot } = useHelp();
  
  const tooltipId = useRef(Math.random().toString(36).substr(2, 9));
  const shouldShowTooltip = isVisible && isActive && (activeTooltipId === null || activeTooltipId === tooltipId.current);

  useEffect(() => {
    if (!isActive) {
      setIsVisible(false);
      return;
    }

    let targetElement = null;
    let retryTimeout = null;
    let cleanupFunctions = [];

    const handleMouseEnter = (event) => {
      setIsVisible(true);
      setActiveTooltipId(tooltipId.current);
      // Update position immediately when tooltip becomes visible
      updatePosition(event);
      animateRobot();
    };

    const handleMouseMove = (event) => {
      if (isVisible) {
        updatePosition(event);
      }
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
      // Clear active tooltip ID when this tooltip hides
      if (activeTooltipId === tooltipId.current) {
        setActiveTooltipId(null);
      }
    };

    const updatePosition = (event) => {
      if (!event) return;

      const margin = 20;
      const tooltipWidth = 320; // w-80 = 320px
      
      // More accurate dynamic height estimation based on content
      let estimatedHeight = 80; // Base padding and margins
      
      // Title height (always present)
      estimatedHeight += 28; // ~1.5rem line height + margin
      
      // Content height estimation (rough calculation based on character count)
      if (content) {
        const contentLines = Math.ceil(content.length / 50); // ~50 chars per line
        estimatedHeight += contentLines * 20 + 12; // 20px per line + margin
      }
      
      // Label section
      if (label && labelText) {
        const labelLines = Math.ceil(labelText.length / 50);
        estimatedHeight += 16 + (labelLines * 20) + 12; // label + text + margin
      }
      
      // Additional info
      if (additionalInfo) {
        const infoLines = Math.ceil(additionalInfo.length / 50);
        estimatedHeight += infoLines * 20 + 12;
      }
      
      // Tip section (with background and border)
      if (tip) {
        const tipLines = Math.ceil(tip.length / 45); // Slightly less due to padding
        estimatedHeight += 40 + (tipLines * 20) + 24; // Header + content + padding
      }
      
      const tooltipHeight = Math.max(estimatedHeight, 120); // Minimum reasonable height
      const screenPadding = 20; // Minimum distance from screen edges
      
      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Position tooltip near the mouse cursor
      let pos = {
        top: event.clientY + margin,
        left: event.clientX + margin
      };
      
      // Horizontal boundary checks
      if (pos.left + tooltipWidth > viewportWidth - screenPadding) {
        // If doesn't fit on right, try left of cursor
        pos.left = event.clientX - tooltipWidth - margin;
        
        // If still doesn't fit on left, clamp to screen boundaries
        if (pos.left < screenPadding) {
          pos.left = screenPadding;
        }
      }
      
      // Vertical boundary checks
      const spaceBelow = viewportHeight - event.clientY - margin;
      const spaceAbove = event.clientY - margin;
      
      if (tooltipHeight <= spaceBelow) {
        // Fits below cursor - use original position
        pos.top = event.clientY + margin;
      } else if (tooltipHeight <= spaceAbove) {
        // Fits above cursor
        pos.top = event.clientY - tooltipHeight - margin;
      } else {
        // Doesn't fit in either direction - use the side with more space
        if (spaceBelow >= spaceAbove) {
          pos.top = Math.min(event.clientY + margin, viewportHeight - tooltipHeight - screenPadding);
        } else {
          pos.top = Math.max(screenPadding, event.clientY - tooltipHeight - margin);
        }
      }
      
      // Final boundary enforcement
      if (pos.top < screenPadding) {
        pos.top = screenPadding;
      }
      
      if (pos.top + tooltipHeight > viewportHeight - screenPadding) {
        pos.top = Math.max(screenPadding, viewportHeight - tooltipHeight - screenPadding);
      }

      setPosition(pos);
    };

    const attachListeners = (element) => {
      element.addEventListener('mouseenter', handleMouseEnter);
      element.addEventListener('mousemove', handleMouseMove);
      element.addEventListener('mouseleave', handleMouseLeave);
      
      return () => {
        element.removeEventListener('mouseenter', handleMouseEnter);
        element.removeEventListener('mousemove', handleMouseMove);
        element.removeEventListener('mouseleave', handleMouseLeave);
      };
    };

    const setupTooltip = () => {
      targetElement = document.querySelector(target);
      if (!targetElement) {
        console.warn(`Tooltip target not found: ${target}`);
        return false;
      }
      
      const cleanup = attachListeners(targetElement);
      cleanupFunctions.push(cleanup);
      return true;
    };

    // Try immediately
    if (!setupTooltip()) {
      // If not found, retry after a short delay
      retryTimeout = setTimeout(() => {
        setupTooltip();
      }, 100);
    }

    // Add MutationObserver to watch for dynamically added elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node or any of its children match our target
              const targetElement = node.matches && node.matches(target) 
                ? node 
                : node.querySelector && node.querySelector(target);
              
              if (targetElement) {
                const cleanup = attachListeners(targetElement);
                cleanupFunctions.push(cleanup);
              }
            }
          });
        }
      });
    });

    // Start observing the document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    cleanupFunctions.push(() => observer.disconnect());

    // Add window resize listener to hide tooltips on resize/scroll
    const handleResize = () => {
      if (shouldShowTooltip) {
        // Hide tooltip on resize/scroll since we don't have event coordinates
        setActiveTooltipId(null);
      }
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    
    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      cleanupFunctions.forEach(cleanup => cleanup());
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [target, isActive]);

  if (!shouldShowTooltip) return null;

  // Use opposite theme colors (light tooltip in dark mode, dark tooltip in light mode)
  const bgColor = isDarkMode ? 'bg-white' : 'bg-neutral-900';
  const textColor = isDarkMode ? 'text-neutral-900' : 'text-white';
  const shadowColor = isDarkMode ? 'shadow-lg' : 'shadow-2xl';
  const borderColor = 'border-2 border-lime-500'; // Full lime green border

  return createPortal(
    <div
      className={`fixed z-[10001] w-80 p-5 rounded-xl ${bgColor} ${textColor} ${borderColor} ${shadowColor} pointer-events-none backdrop-blur-sm overflow-hidden`}
      style={{
        top: position.top,
        left: position.left,
        maxHeight: `${Math.min(window.innerHeight - 40, 600)}px` // Never taller than viewport minus padding
      }}
    >
      <div className="flex items-start h-full">
        <div className="flex-1 overflow-y-auto max-h-full">
          {/* Headline */}
          <div className="font-semibold text-base mb-2 leading-tight text-lime-500">{title}</div>
          
          {/* Description */}
          <div className="text-sm leading-relaxed opacity-90 mb-3">{content}</div>
          
          {/* Label and Label Text */}
          {label && labelText && (
            <div className="mb-3">
              <div className="text-xs font-bold text-lime-500 uppercase tracking-wide mb-1">{label}</div>
              <div className="text-sm opacity-80">{labelText}</div>
            </div>
          )}
          
          {/* Additional Info */}
          {additionalInfo && (
            <div className="text-sm opacity-80 mb-3">{additionalInfo}</div>
          )}
          
          {/* Tip */}
          {tip && (
            <div className="bg-lime-500 bg-opacity-10 border border-lime-500 border-opacity-30 rounded-lg p-3 mt-3">
              <div className="flex items-center mb-2">
                <Lightbulb size={16} className="text-lime-500 mr-2" />
                <div className="text-sm font-bold text-lime-500 uppercase tracking-wide">Tip</div>
              </div>
              <div className="text-sm font-medium text-lime-500">{tip}</div>
            </div>
          )}
        </div>
      </div>

    </div>,
    document.body
  );
};

export default HoverTooltip;
